import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import {
  MENU_CATALOG,
  applyMenuTriState,
  menuTriState,
  type MenuTriState,
  type RoleWithPermissions,
} from '@dcc-erp/shared';

import { AppModal } from '../components/AppModal';
import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import {
  useCreateRole,
  useDeleteRole,
  usePermissionCatalog,
  useRoles,
  useSetRolePermissions,
  useUpdateRole,
} from '../lib/queries';

export const Route = createFileRoute('/settings/roles')({ component: RolesPageInner });

export function RolesPageInner() {
  const { data: roles = [], isPending, error } = useRoles();
  const [editing, setEditing] = useState<RoleWithPermissions | null>(null);
  const [editingRole, setEditingRole] = useState<RoleWithPermissions | null>(null);
  const [creating, setCreating] = useState(false);
  const del = useDeleteRole();

  return (
    <div className="page">
      <PageHead
        crumbs={['ระบบ', 'บทบาท & สิทธิ์']}
        title="บทบาท & สิทธิ์ (Roles & Permissions)"
        actions={<HelpButton guide="settings-roles" />}
      />
      <div style={{ marginBottom: 12 }}>
        <button className="btn primary" onClick={() => setCreating(true)}>
          + สร้างบทบาท
        </button>
      </div>
      <div className="tbl-shell">
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 160 }}>รหัส</th>
              <th>ชื่อ</th>
              <th style={{ width: 110 }}>สิทธิ์</th>
              <th style={{ width: 90 }}>ระบบ</th>
              <th style={{ width: 160 }} />
            </tr>
          </thead>
          <tbody>
            {isPending ? (
              <tr>
                <td colSpan={5} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  กำลังโหลด…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  colSpan={5}
                  style={{ padding: 24, textAlign: 'center', color: 'var(--negative)' }}
                >
                  โหลดล้มเหลว: {error.message}
                </td>
              </tr>
            ) : (
              roles.map((r) => (
                <tr key={r.id}>
                  <td className="code">
                    <b>{r.code}</b>
                  </td>
                  <td>{r.nameTh}</td>
                  <td className="tnum">
                    {r.permissions.includes('*') ? 'ทั้งหมด' : r.permissions.length}
                  </td>
                  <td className="muted">{r.isSystem ? 'ใช่' : '—'}</td>
                  <td style={{ display: 'flex', gap: 6 }}>
                    <button className="btn sm" onClick={() => setEditing(r)}>
                      แก้สิทธิ์
                    </button>
                    <button className="btn sm ghost" onClick={() => setEditingRole(r)}>
                      แก้ไข
                    </button>
                    {!r.isSystem && (
                      <button
                        className="btn sm ghost"
                        onClick={() => {
                          if (confirm(`ลบบทบาท ${r.code}?`)) void del.mutateAsync(r.id);
                        }}
                      >
                        ลบ
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {editing && <PermissionModal role={editing} onClose={() => setEditing(null)} />}
      {editingRole && <EditRoleModal role={editingRole} onClose={() => setEditingRole(null)} />}
      {creating && <CreateRoleModal onClose={() => setCreating(false)} />}
    </div>
  );
}

function PermissionModal({ role, onClose }: { role: RoleWithPermissions; onClose: () => void }) {
  const { data: catalog = {} } = usePermissionCatalog();
  const setPerms = useSetRolePermissions();
  const isStar = role.permissions.includes('*');
  const [selected, setSelected] = useState<Set<string>>(new Set(role.permissions));
  const [advanced, setAdvanced] = useState(false);

  function setMenu(menuKey: string, state: MenuTriState) {
    const m = MENU_CATALOG.find((x) => x.menuKey === menuKey);
    if (!m) return;
    setSelected((prev) => applyMenuTriState(m, state, prev));
  }

  function toggleKey(k: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }

  async function save() {
    await setPerms.mutateAsync({ id: role.id, data: { permissionKeys: [...selected] } });
    onClose();
  }

  return (
    <AppModal
      title={`สิทธิ์ของบทบาท ${role.code}`}
      subtitle={isStar ? 'บทบาทนี้มีสิทธิ์ทั้งหมด (*) — แก้ไม่ได้' : role.nameTh}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={isStar || setPerms.isPending}
            onClick={() => void save()}
          >
            บันทึก
          </button>
        </>
      }
    >
      {isStar ? (
        <div className="muted">บทบาท ADMIN ถือสิทธิ์ทั้งหมดโดยอัตโนมัติ</div>
      ) : (
        <>
          <div style={{ marginBottom: 8 }}>
            <button className="btn sm ghost" onClick={() => setAdvanced((v) => !v)}>
              {advanced ? 'มุมมองเมนู (Tri-state)' : 'ขั้นสูง (รายสิทธิ์)'}
            </button>
          </div>
          {advanced ? (
            // Per-key grid — surgical edits (preserves precise maker/approver SoD subsets).
            Object.entries(catalog).map(([resource, keys]) => (
              <div key={resource} style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{resource}</div>
                {keys.map((k) => (
                  <label
                    key={k}
                    style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(k)}
                      onChange={() => toggleKey(k)}
                    />
                    <span className="code">{k}</span>
                  </label>
                ))}
              </div>
            ))
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>เมนู</th>
                  <th style={{ width: 360 }}>สิทธิ์</th>
                </tr>
              </thead>
              <tbody>
                {MENU_CATALOG.map((m) => {
                  const state = menuTriState(m, selected);
                  const hasRead = m.readKeys.length > 0;
                  return (
                    <tr key={m.menuKey}>
                      <td>{m.labelTh}</td>
                      <td>
                        <TriToggle
                          menuKey={m.menuKey}
                          value={state}
                          hasReadTier={hasRead}
                          onChange={(s) => setMenu(m.menuKey, s)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}
    </AppModal>
  );
}

function TriToggle({
  menuKey,
  value,
  hasReadTier,
  onChange,
}: {
  menuKey: string;
  value: MenuTriState | 'custom';
  hasReadTier: boolean;
  onChange: (s: MenuTriState) => void;
}) {
  const opts: { s: MenuTriState; label: string; show: boolean }[] = [
    { s: 'none', label: 'ไม่มีสิทธิ์', show: true },
    { s: 'read', label: 'อ่านอย่างเดียว', show: hasReadTier },
    { s: 'full', label: 'ทำงานได้เต็ม', show: true },
  ];
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {opts
        .filter((o) => o.show)
        .map((o) => (
          <button
            key={o.s}
            data-testid={`tri-${menuKey}-${o.s}`}
            className={`btn sm ${value === o.s ? 'primary' : 'ghost'}`}
            onClick={() => onChange(o.s)}
          >
            {o.label}
          </button>
        ))}
      {value === 'custom' && (
        <span className="muted" style={{ alignSelf: 'center' }}>
          กำหนดเอง
        </span>
      )}
    </div>
  );
}

function EditRoleModal({ role, onClose }: { role: RoleWithPermissions; onClose: () => void }) {
  const updateRole = useUpdateRole();
  const [nameTh, setNameTh] = useState(role.nameTh);
  const [description, setDescription] = useState(role.description ?? '');

  async function save() {
    await updateRole.mutateAsync({
      id: role.id,
      data: { nameTh, description: description || undefined },
    });
    onClose();
  }

  return (
    <AppModal
      title="แก้ไขบทบาท"
      subtitle={role.code}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={!nameTh || updateRole.isPending}
            onClick={() => void save()}
          >
            บันทึก
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span>ชื่อ (ภาษาไทย)</span>
          <input
            className="input"
            value={nameTh}
            onChange={(e) => setNameTh(e.target.value)}
            placeholder="เช่น นักบัญชีสร้างใบสำคัญ"
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span>คำอธิบาย (ไม่บังคับ)</span>
          <input
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="อธิบายบทบาทนี้"
          />
        </label>
      </div>
    </AppModal>
  );
}

function CreateRoleModal({ onClose }: { onClose: () => void }) {
  const createRole = useCreateRole();
  const [code, setCode] = useState('');
  const [nameTh, setNameTh] = useState('');
  const [description, setDescription] = useState('');

  async function save() {
    await createRole.mutateAsync({ code, nameTh, description: description || undefined });
    onClose();
  }

  return (
    <AppModal
      title="สร้างบทบาทใหม่"
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={!code || !nameTh || createRole.isPending}
            onClick={() => void save()}
          >
            สร้าง
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span>รหัสบทบาท (UPPER_SNAKE)</span>
          <input
            className="input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
            placeholder="เช่น ACCOUNTANT_MAKER"
            maxLength={40}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span>ชื่อ (ภาษาไทย)</span>
          <input
            className="input"
            value={nameTh}
            onChange={(e) => setNameTh(e.target.value)}
            placeholder="เช่น นักบัญชีสร้างใบสำคัญ"
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span>คำอธิบาย (ไม่บังคับ)</span>
          <input
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="อธิบายบทบาทนี้"
          />
        </label>
      </div>
    </AppModal>
  );
}
